import React, { useState } from "react";
import {
    Box,
    ButtonBase,
    Collapse,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, Tune } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { OrderChannelSimple } from "../../../services/orderChannelService";
import { formatCurrencyBDT } from "../../../utils/currency";
import { OrderFormData, OrderStatus } from "./orderFormSchema";

const STATUS_LABELS: Record<OrderStatus, string> = {
    DRAFT: "Draft",
    PENDING: "Pending",
    CONFIRMED: "Confirmed",
    SHIPPED: "Shipped",
    DELIVERED: "Delivered",
    CANCELLED: "Cancelled",
};

interface Props {
    control: Control<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    /** Draft is offered for new orders and orders that are still drafts. */
    allowDraft: boolean;
    orderChannels: OrderChannelSimple[];
    values: {
        status: OrderStatus;
        order_channel_id?: string;
        delivery_cost: number;
        show_gifts_on_bill: boolean;
    };
}

/**
 * The settings that usually stay as they are — status, channel, what the
 * delivery costs us, gifts on the bill. Folded away, but the folded row
 * says what each one is set to.
 */
export const MoreOptions: React.FC<Props> = ({ control, errors, allowDraft, orderChannels, values }) => {
    const [open, setOpen] = useState(false);
    const channel = orderChannels.find((c) => c.order_channel_id === values.order_channel_id)?.name;
    const summary = [
        STATUS_LABELS[values.status] ?? values.status,
        channel ?? "No channel",
        `our cost ${formatCurrencyBDT(Number(values.delivery_cost) || 0, { minimumFractionDigits: 0 })}`,
        values.show_gifts_on_bill ? "gifts on bill" : "gifts hidden on bill",
    ].join(" · ");

    return (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
            <ButtonBase
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                sx={{ width: "100%", justifyContent: "flex-start", gap: 1, px: 1.5, py: 1.25, textAlign: "left", borderRadius: 1 }}
            >
                <Tune fontSize="small" color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                        More options
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {summary}
                    </Typography>
                </Box>
                {open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
            </ButtonBase>
            <Collapse in={open}>
                <Box sx={{ display: "grid", gap: 1.5, px: 1.5, pb: 1.5, pt: 0.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth size="small" required error={!!errors.status}>
                                <InputLabel>Status</InputLabel>
                                <Select {...field} label="Status">
                                    {(Object.keys(STATUS_LABELS) as OrderStatus[])
                                        .filter((s) => s !== "DRAFT" || allowDraft)
                                        .map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {STATUS_LABELS[s]}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="order_channel_id"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth size="small">
                                <InputLabel>Order channel</InputLabel>
                                <Select
                                    {...field}
                                    value={orderChannels.some((c) => c.order_channel_id === field.value) ? field.value : ""}
                                    label="Order channel"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {orderChannels.map((c) => (
                                        <MenuItem key={c.order_channel_id} value={c.order_channel_id}>
                                            {c.name} ({c.type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="delivery_cost"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ""}
                                type="number"
                                size="small"
                                label="Delivery cost (we pay)"
                                inputProps={{ min: 0, step: 1 }}
                                InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                                error={!!errors.delivery_cost}
                                helperText={errors.delivery_cost?.message}
                            />
                        )}
                    />
                    <Controller
                        name="show_gifts_on_bill"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                label="Show gifts on the bill"
                            />
                        )}
                    />
                </Box>
            </Collapse>
        </Box>
    );
};

export default MoreOptions;
